import { useState, useEffect } from 'react';
import { Bell, Loader as Loader2 } from 'lucide-react';
import { getUserPreferences, upsertUserPreferences } from '@/lib/api';

export default function NotificationsSection() {
  const [emailNewPayments, setEmailNewPayments] = useState(false);
  const [emailRateChanges, setEmailRateChanges] = useState(false);
  const [emailLowProfit, setEmailLowProfit] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    getUserPreferences().then((prefs) => {
      if (prefs) {
        setEmailNewPayments(prefs.email_new_payments);
        setEmailRateChanges(prefs.email_rate_changes);
        setEmailLowProfit(prefs.email_low_profit);
      }
      setLoaded(true);
    });
  }, []);

  async function toggle(field: 'email_new_payments' | 'email_rate_changes' | 'email_low_profit', value: boolean) {
    setSaving(field);
    try {
      await upsertUserPreferences({ [field]: value });
    } catch {
      if (field === 'email_new_payments') setEmailNewPayments(!value);
      if (field === 'email_rate_changes') setEmailRateChanges(!value);
      if (field === 'email_low_profit') setEmailLowProfit(!value);
    } finally {
      setSaving(null);
    }
  }

  if (!loaded) {
    return (
      <div className="flex items-center gap-3 p-4">
        <Loader2 size={16} className="animate-spin text-white" />
        <span className="text-sm text-white">Loading preferences...</span>
      </div>
    );
  }

  const toggles = [
    {
      label: 'New payment alerts',
      desc: 'Email when a new payment is received',
      field: 'email_new_payments' as const,
      value: emailNewPayments,
      setter: setEmailNewPayments,
    },
    {
      label: 'Exchange rate alerts',
      desc: 'Email when rate changes more than 0.5',
      field: 'email_rate_changes' as const,
      value: emailRateChanges,
      setter: setEmailRateChanges,
    },
    {
      label: 'Low profit alerts',
      desc: 'Email when net profit drops below threshold',
      field: 'email_low_profit' as const,
      value: emailLowProfit,
      setter: setEmailLowProfit,
    },
  ];

  return (
    <div>
      <h2 className="text-[11px] font-semibold text-white uppercase tracking-wider mb-2.5 px-1">Notifications</h2>
      <div className="glass-panel rounded-2xl p-2">
        {toggles.map((t, idx) => (
          <div key={t.field}>
            {idx > 0 && <div className="h-px bg-white/10 mx-3 my-1" />}
            <div className="flex items-center gap-3 py-2.5 px-3">
              <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
                <Bell size={15} className="text-teal-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{t.label}</p>
                <p className="text-[11px] text-white">{t.desc}</p>
              </div>
              <button
                onClick={() => {
                  const next = !t.value;
                  t.setter(next);
                  toggle(t.field, next);
                }}
                disabled={saving === t.field}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${t.value ? 'bg-teal-500' : 'bg-slate-300'}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 flex items-center justify-center ${t.value ? 'translate-x-5' : 'translate-x-0'}`}>
                  {saving === t.field && <Loader2 size={10} className="animate-spin text-white" />}
                </div>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
